import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { usePlus } from '../context/PlusContext';
import { X, Plus, Trash2 } from 'lucide-react';
import { Inversion, TipoInversion, ComisionTramo } from '../types';
import { TIPO_INVERSION_LABEL, TIPO_INVERSION_ICON, TRAMOS_IPA_DEFAULT } from '../utils/inversionUtils';
import { aNumero, AVISO_IMPORTE, PATRON_IMPORTE } from '../utils/numeros';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';

interface Props {
  onClose: () => void;
  inversionEditar?: Inversion;
}

const TIPOS: TipoInversion[] = [
  'etf', 'acciones', 'fondo', 'cripto',
  'deposito', 'plan_pensiones', 'ipa', 'crowdlending', 'inmueble', 'otro',
];

export const ModalAgregarInversion: React.FC<Props> = ({ onClose, inversionEditar }) => {
  useCerrarConEscape(onClose);
  const { crearInversion, actualizarInversion } = useData();
  const { ofrecerSiEsLimite } = usePlus();
  const modo = inversionEditar ? 'editar' : 'crear';

  const [nombre,      setNombre]      = useState(inversionEditar?.nombre      || '');
  const [tipo,        setTipo]        = useState<TipoInversion>(inversionEditar?.tipo || 'etf');
  const [broker,      setBroker]      = useState(inversionEditar?.broker      || '');
  const [ticker,      setTicker]      = useState(inversionEditar?.ticker      || '');
  const [fechaInicio, setFechaInicio] = useState(
    inversionEditar ? new Date(inversionEditar.fechaInicio).toISOString().split('T')[0] : '',
  );
  const [valorActual, setValorActual] = useState(
    inversionEditar ? inversionEditar.valorActual.toString() : '0',
  );
  const [notas,  setNotas]  = useState(inversionEditar?.notas || '');
  const [activo, setActivo] = useState(inversionEditar?.activo ?? true);

  // Comisión
  const [comisionTipo, setComisionTipo] = useState<'ninguna' | 'porcentaje_fijo' | 'escalonada'>(
    inversionEditar?.comision.tipo || 'ninguna',
  );
  const [porcFijo,  setPorcFijo]  = useState(
    inversionEditar?.comision.porcentajeFijo?.toString() || '',
  );
  const [tramos, setTramos] = useState<ComisionTramo[]>(
    inversionEditar?.comision.tramos ?? (tipo === 'ipa' ? TRAMOS_IPA_DEFAULT : []),
  );

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const inputCls = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm";
  const labelCls = "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5";

  const handleTipoChange = (t: TipoInversion) => {
    setTipo(t);
    // Para IPA, sugerir comisión escalonada con tramos por defecto
    if (t === 'ipa' && comisionTipo === 'ninguna') {
      setComisionTipo('escalonada');
      setTramos(TRAMOS_IPA_DEFAULT);
    }
  };

  const addTramo = () => setTramos(prev => [...prev, { aniosHasta: 999, porcentaje: 0 }]);
  const removeTramo = (i: number) => setTramos(prev => prev.filter((_, idx) => idx !== i));
  const updateTramo = (i: number, field: keyof ComisionTramo, val: number) =>
    setTramos(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: val } : t));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!nombre.trim() || !broker.trim() || !fechaInicio) {
      setError('Nombre, broker y fecha de inicio son obligatorios'); return;
    }
    if (comisionTipo === 'escalonada' && tramos.length === 0) {
      setError('Añade al menos un tramo de comisión'); return;
    }
    setLoading(true);
    try {
      const comision = comisionTipo === 'ninguna'
        ? { tipo: 'ninguna' as const }
        : comisionTipo === 'porcentaje_fijo'
          ? { tipo: 'porcentaje_fijo' as const, porcentajeFijo: aNumero(porcFijo) || 0 }
          : { tipo: 'escalonada' as const, tramos };

      const datos: Omit<Inversion, 'id' | 'usuarioId' | 'createdAt'> = {
        nombre: nombre.trim(),
        tipo,
        broker: broker.trim(),
        ...(ticker.trim() ? { ticker: ticker.trim() } : {}),
        fechaInicio: new Date(fechaInicio),
        moneda: 'EUR',
        activo,
        ...(notas.trim() ? { notas: notas.trim() } : {}),
        comision,
        valorActual: aNumero(valorActual) || 0,
      };

      if (modo === 'editar' && inversionEditar) {
        await actualizarInversion(inversionEditar.id, datos);
      } else {
        await crearInversion(datos);
      }
      onClose();
    } catch (err: any) {
      // Sin hueco en el plan gratis: en vez del error, la hoja de Plus
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      setError(err.message || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center capa-hoja z-50">
      <div className="hoja bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-full overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 z-10">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {modo === 'editar' ? 'Editar inversión' : 'Nueva inversión'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>
          )}

          {/* Nombre */}
          <div>
            <label className={labelCls}>Nombre del producto</label>
            <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
              className={inputCls} placeholder="Ej: Vanguard S&P 500 ETF" required />
          </div>

          {/* Tipo */}
          <div>
            <label className={labelCls}>Tipo</label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {TIPOS.map(t => (
                <button key={t} type="button" onClick={() => handleTipoChange(t)}
                  className={`flex flex-col items-center gap-0.5 p-2 rounded-xl border-2 transition text-center ${
                    tipo === t
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-transparent bg-gray-50 dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}>
                  <span className="text-xl">{TIPO_INVERSION_ICON[t]}</span>
                  <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 leading-tight">
                    {TIPO_INVERSION_LABEL[t]}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Broker + Ticker */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Broker / Entidad</label>
              <input type="text" value={broker} onChange={e => setBroker(e.target.value)}
                className={inputCls} placeholder="MyInvestor, Caser…" required />
            </div>
            <div>
              <label className={labelCls}>Ticker <span className="font-normal text-gray-400">(opcional)</span></label>
              <input type="text" value={ticker} onChange={e => setTicker(e.target.value)}
                className={inputCls} placeholder="VOO, BTC…" />
            </div>
          </div>

          {/* Fecha inicio + Valor actual */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Fecha de inicio</label>
              <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)}
                className={inputCls} required />
            </div>
            <div>
              <label className={labelCls}>Valor actual (€)</label>
              <input type="text" inputMode="decimal" pattern={PATRON_IMPORTE} title={AVISO_IMPORTE} value={valorActual}
                onChange={e => setValorActual(e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Comisión */}
          <div>
            <label className={labelCls}>Estructura de comisión</label>
            <div className="flex gap-2 mb-3">
              {([
                { v: 'ninguna'          as const, l: 'Ninguna'     },
                { v: 'porcentaje_fijo'  as const, l: '% Fijo'      },
                { v: 'escalonada'       as const, l: 'Escalonada'  },
              ]).map(({ v, l }) => (
                <button key={v} type="button" onClick={() => setComisionTipo(v)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
                    comisionTipo === v ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}>
                  {l}
                </button>
              ))}
            </div>

            {comisionTipo === 'porcentaje_fijo' && (
              <div className="flex items-center gap-2">
                <input type="text" inputMode="decimal" pattern={PATRON_IMPORTE} title={AVISO_IMPORTE} value={porcFijo}
                  onChange={e => setPorcFijo(e.target.value)} className={`${inputCls} w-32`}
                  placeholder="1,5" />
                <span className="text-sm text-gray-600 dark:text-gray-400">% sobre cada aportación</span>
              </div>
            )}

            {comisionTipo === 'escalonada' && (
              <div className="space-y-2">
                {tramos.map((tramo, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Hasta año</span>
                    <input type="number" inputMode="numeric" min="0" step="1"
                      value={tramo.aniosHasta >= 999 ? '' : tramo.aniosHasta}
                      onChange={e => updateTramo(i, 'aniosHasta', parseInt(e.target.value) || 999)}
                      className={`${inputCls} w-20`} placeholder="∞" />
                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">→</span>
                    <input type="number" min="0" max="100" step="0.1"
                      value={tramo.porcentaje}
                      onChange={e => updateTramo(i, 'porcentaje', parseFloat(e.target.value) || 0)}
                      className={`${inputCls} w-20`} />
                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">%</span>
                    <button type="button" onClick={() => removeTramo(i)}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addTramo}
                  className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  <Plus className="w-3.5 h-3.5" /> Añadir tramo
                </button>
                <p className="text-xs text-gray-400">
                  El último tramo con año ∞ aplica al resto de aportaciones.
                </p>
              </div>
            )}
          </div>

          {/* Notas + Activo */}
          <div>
            <label className={labelCls}>Notas <span className="font-normal text-gray-400">(opcional)</span></label>
            <textarea value={notas} onChange={e => setNotas(e.target.value)}
              className={`${inputCls} resize-none`} rows={2} placeholder="Cualquier anotación…" />
          </div>

          {modo === 'editar' && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={activo} onChange={e => setActivo(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded" />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Posición activa</span>
            </label>
          )}

          <div className="flex gap-3 pt-1 pb-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl text-sm transition hover:bg-gray-50 dark:hover:bg-gray-700">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm transition disabled:opacity-50">
              {loading ? 'Guardando…' : modo === 'editar' ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
